import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search } from 'lucide-react';
import { useCurrency } from '@/context/CurrencyContext';
import { useLanguage } from '@/context/LanguageContext';
import SEO from '@/components/SEO';
import SocialStrip from '@/components/SocialStrip';

const TYPES = [
  { key: 'all', tkey: 'shop.all', fallback: 'All' },
  { key: 'phone_case', tkey: 'shop.phoneCases', fallback: 'Phone Cases' },
  { key: 'sticker', tkey: 'shop.stickers', fallback: 'Stickers' },
  { key: 'label', tkey: 'shop.labels', fallback: 'Labels' },
];

const TYPE_META = {
  all: { tk: 'shop.title', dk: 'shop.desc.all', title: 'Shop Custom Prints', desc: 'Browse premium custom-printed phone cases, stickers, and labels. Design your own or pick from thousands of templates.' },
  phone_case: { tk: 'shop.phoneCases', dk: 'shop.desc.phone_case', title: 'Custom Phone Cases', desc: 'Premium quality custom iPhone cases with edge-to-edge print. Choose from clear, matte, and frosted finishes.' },
  sticker: { tk: 'shop.stickers', dk: 'shop.desc.sticker', title: 'Custom Vinyl Stickers', desc: 'Waterproof vinyl stickers in every shape and size. Glossy, matte, holographic finishes for laptops, bottles, and more.' },
  label: { tk: 'shop.labels', dk: 'shop.desc.label', title: 'Custom Product Labels', desc: 'Vibrant custom labels for packaging, jars, bottles and branding. Glossy, kraft, and clear finishes available.' },
};

const Shop = () => {
  const { type: typeParam } = useParams();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [activeType, setActiveType] = useState(typeParam || 'all');
  const [socialMap, setSocialMap] = useState({});
  const { format } = useCurrency();
  const { t } = useLanguage();

  useEffect(() => { setActiveType(typeParam || 'all'); }, [typeParam]);

  useEffect(() => {
    const params = {};
    if (activeType && activeType !== 'all') params.type = activeType;
    api.get('/products', { params }).then(({ data }) => setProducts(data));
  }, [activeType]);

  const filtered = useMemo(() => {
    if (!search) return products;
    return products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
  }, [products, search]);

  const productIds = useMemo(() => filtered.map((p) => p.id), [filtered]);
  useEffect(() => {
    if (productIds.length === 0) { setSocialMap({}); return; }
    api.post('/social/batch', { items: productIds.map((id) => ({ entity_type: 'product', id })) })
      .then(({ data }) => {
        const m = {};
        for (const s of data) m[s.entity_id] = s;
        setSocialMap(m);
      })
      .catch(() => {});
  }, [productIds]);

  const meta = TYPE_META[activeType] || TYPE_META.all;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <SEO title={meta.title} description={meta.desc} path={typeParam ? `/shop/${typeParam}` : '/shop'} />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">{t(meta.tk, meta.title)}</h1>
          <p className="mt-1" style={{ color: 'var(--jw-text-muted)' }}>{activeType === 'all' ? t('shop.subtitle', 'Pick a product and start designing.') : t(meta.dk, meta.desc)}</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--jw-text-subtle)' }} />
          <Input data-testid="shop-search-input" placeholder={t('shop.search', 'Search products')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-full sm:w-64" />
        </div>
      </div>

      <div className="mt-6 -mx-4 sm:mx-0 overflow-x-auto jw-scrollbar">
        <div className="px-4 sm:px-0 min-w-max">
          <Tabs value={activeType} onValueChange={setActiveType}>
            <TabsList>
              {TYPES.map(tab => (
                <TabsTrigger key={tab.key} value={tab.key} data-testid={`shop-tab-${tab.key}`}>{t(tab.tkey, tab.fallback)}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-8">
        {filtered.map(p => (
          <Card key={p.id} className="jw-card overflow-hidden group flex flex-col" data-testid={`shop-product-${p.id}-card`}>
            <Link to={`/product/${p.id}`} className="block">
              <div className="aspect-square overflow-hidden" style={{ background: 'var(--jw-bg-elev)' }}>
                <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            </Link>
            <div className="p-4 flex flex-col gap-3 flex-1">
              <Link to={`/product/${p.id}`} className="block">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{p.name}</h3>
                  <span className="text-sm" style={{ color: 'var(--jw-text-muted)' }}>{format(p.price)}</span>
                </div>
                <div className="flex gap-1 mt-2 flex-wrap">
                  {p.variants?.slice(0, 3).map(v => (
                    <Badge key={v} variant="secondary" className="text-[10px] font-normal">{v}</Badge>
                  ))}
                </div>
              </Link>
              <SocialStrip
                entityType="product"
                entityId={p.id}
                initial={socialMap[p.id]}
                onChange={(next) => setSocialMap((m) => ({ ...m, [p.id]: next }))}
              />
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16" style={{ color: 'var(--jw-text-muted)' }}>{t('shop.noneFound', 'No products found.')}</div>
        )}
      </div>
    </div>
  );
};

export default Shop;
